import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Users, Send, MessageSquare, Activity, Lock, Loader2, Inbox, AlertTriangle } from 'lucide-react';
import { apiGet } from '../api/client';
import { listCampaigns } from '../api/campaigns';
import { listProfiles } from '../api/scrape';
import { getPendingCount } from '../api/replies';

const POLL_INTERVAL = 4000;

function StatCard({ title, value, subtitle, icon: Icon, locked, loading }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 relative">
      {locked && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10">
          <div className="flex items-center gap-1.5 text-slate-400 text-xs font-medium">
            <Lock className="w-3.5 h-3.5" />
            <span>Coming Soon</span>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">{title}</p>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-slate-300 mt-1" />
      ) : (
        <p className="text-3xl font-bold text-slate-900">{value}</p>
      )}
      <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
    </div>
  );
}

function StatusDot({ status }) {
  if (status === 'completed' || status === 'active') return <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />;
  if (status === 'generating' || status === 'pending' || status === 'scraping' || status === 'profiled' || status === 'generating_icp') return <span className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse inline-block" />;
  if (status === 'failed') return <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />;
  if (status === 'draft') return <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />;
  return <span className="w-2 h-2 rounded-full bg-slate-300 inline-block" />;
}

function statusLabel(status) {
  const map = {
    pending: 'Starting...',
    scraping: 'Scraping website...',
    profiled: 'Profile done, generating ICP...',
    generating_icp: 'Generating ICP & messaging...',
    completed: 'Completed',
    failed: 'Failed',
    generating: 'Generating sequences...',
    draft: 'Draft',
    active: 'Active',
    paused: 'Paused',
    stopped: 'Stopped',
  };
  return map[status] || status;
}

export default function Dashboard() {
  const [stats, setStats] = useState({ prospects: 0, emailsSent: 0, campaigns: 0 });
  const [recentCampaigns, setRecentCampaigns] = useState([]);
  const [activeProfiles, setActiveProfiles] = useState([]);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [pending, setPending] = useState({ total: 0, hotLeads: 0, pendingApproval: 0 });
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const [prospectsRes, campaignsRes, profilesRes, pendingRes] = await Promise.all([
        apiGet('/prospects/list').catch(() => ({ prospects: [] })),
        listCampaigns().catch(() => ({ campaigns: [] })),
        listProfiles().catch(() => ({ profiles: [] })),
        getPendingCount().catch(() => ({ total: 0, hotLeads: 0, pendingApproval: 0 })),
      ]);
      setPending(pendingRes);

      const prospects = prospectsRes.prospects || [];
      const campaigns = campaignsRes.campaigns || [];
      const profiles = (profilesRes.profiles || []).filter(p => !p.deleted);

      let totalSent = 0;
      campaigns.forEach(c => { totalSent += c.stats?.sent || 0; });

      setStats({
        prospects: prospects.length,
        emailsSent: totalSent,
        campaigns: campaigns.length,
      });
      setRecentCampaigns(campaigns.slice(0, 5));

      // Find in-progress voice profiles
      const inProgress = profiles.filter(p =>
        ['pending', 'scraping', 'profiled', 'generating_icp'].includes(p.status)
      );
      setActiveProfiles(inProgress);

      // Find active/generating campaigns
      const runningCampaigns = campaigns.filter(c =>
        ['generating', 'active'].includes(c.status)
      );
      setActiveCampaigns(runningCampaigns);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();

    // Poll every 4s for live updates
    pollRef.current = setInterval(() => {
      load();
    }, POLL_INTERVAL);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">
          Welcome back. Here's what's happening with your outbound.
        </p>
      </div>

      {/* Pending Approval Banner */}
      {pending.total > 0 && (
        <Link to="/inboxes"
          className="block bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-xl p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Inbox className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-amber-900">
                    {pending.total} {pending.total === 1 ? 'reply' : 'replies'} need your attention
                  </p>
                  {pending.hotLeads > 0 && (
                    <span className="flex items-center gap-1 text-xs font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                      <AlertTriangle className="w-3 h-3" />
                      {pending.hotLeads} hot {pending.hotLeads === 1 ? 'lead' : 'leads'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-amber-700 mt-0.5">Review AI-drafted replies and approve before sending</p>
              </div>
            </div>
            <span className="text-sm font-medium text-amber-700">Go to Inbox →</span>
          </div>
        </Link>
      )}

      {/* Live Activity Banner */}
      {(activeProfiles.length > 0 || activeCampaigns.length > 0) && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
            <span className="text-sm font-semibold text-blue-800">Live Activity</span>
            <span className="text-xs text-blue-500">Auto-refreshing every {POLL_INTERVAL / 1000}s</span>
          </div>

          {activeProfiles.map(p => (
            <Link
              key={p.jobId}
              to="/voice-profile"
              className="flex items-center gap-3 px-3 py-2 bg-white/70 rounded-lg hover:bg-white transition-colors"
            >
              <StatusDot status={p.status} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  Voice Profile: {p.companyName || p.url || p.jobId.slice(0, 8)}
                </p>
                <p className="text-xs text-slate-500">{statusLabel(p.status)}</p>
              </div>
              <Activity className="w-4 h-4 text-blue-500" />
            </Link>
          ))}

          {activeCampaigns.map(c => (
            <Link
              key={c.campaignId}
              to={`/campaigns/${c.campaignId}`}
              className="flex items-center gap-3 px-3 py-2 bg-white/70 rounded-lg hover:bg-white transition-colors"
            >
              <StatusDot status={c.status} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  Campaign: {c.name}
                </p>
                <p className="text-xs text-slate-500">
                  {statusLabel(c.status)}
                  {c.status === 'generating' && c.stats?.generated != null && (
                    <> &mdash; {c.stats.generated}/{c.stats.total || '?'} sequences</>
                  )}
                  {c.status === 'active' && (
                    <> &mdash; {c.stats?.sent || 0} sent</>
                  )}
                </p>
              </div>
              <Send className="w-4 h-4 text-blue-500" />
            </Link>
          ))}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Prospects"
          value={stats.prospects}
          subtitle="Total in pipeline"
          icon={Users}
          loading={loading}
        />
        <StatCard
          title="Emails Sent"
          value={stats.emailsSent}
          subtitle="Across all campaigns"
          icon={Send}
          loading={loading}
        />
        <StatCard
          title="Campaigns"
          value={stats.campaigns}
          subtitle="Total created"
          icon={Activity}
          loading={loading}
        />
        <StatCard
          title="Reply Rate"
          value="--"
          subtitle="Avg across campaigns"
          icon={MessageSquare}
          locked
        />
      </div>

      {/* Quick Actions + Recent */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link
              to="/voice-profile"
              className="flex items-center gap-3 px-4 py-3 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors text-sm font-medium"
            >
              <Activity className="w-4 h-4" />
              Generate Voice Profile & ICP
            </Link>
            <Link
              to="/prospects"
              className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-50 text-slate-700 hover:bg-slate-100 transition-colors text-sm font-medium"
            >
              <Users className="w-4 h-4" />
              Discover Prospects
            </Link>
            <Link
              to="/campaigns"
              className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-50 text-slate-700 hover:bg-slate-100 transition-colors text-sm font-medium"
            >
              <Send className="w-4 h-4" />
              Create Campaign
            </Link>
          </div>
        </div>

        {/* Recent Campaigns */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Recent Campaigns</h2>
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
          ) : recentCampaigns.length === 0 ? (
            <p className="text-sm text-slate-500">
              No campaigns yet. Create a voice profile, discover prospects, then launch a campaign.
            </p>
          ) : (
            <div className="space-y-3">
              {recentCampaigns.map(c => (
                <Link
                  key={c.campaignId}
                  to={`/campaigns/${c.campaignId}`}
                  className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <StatusDot status={c.status} />
                    <div>
                      <p className="text-sm font-medium text-slate-900">{c.name}</p>
                      <p className="text-xs text-slate-400">{new Date(c.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      c.status === 'draft' ? 'bg-blue-100 text-blue-700' :
                      c.status === 'generating' ? 'bg-yellow-100 text-yellow-700' :
                      c.status === 'active' ? 'bg-green-100 text-green-700' :
                      c.status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {c.status}
                    </span>
                    <p className="text-xs text-slate-400 mt-0.5">{c.stats?.sent || 0} sent</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
